import { clamp } from "../utils/math";
import type { GenreBlend, GenreBlendInput, GenreFeatures } from "./musicTypes";

/**
 * Turns the three genre sliders into weights on generation rules.
 *
 * The hard rule from the spec: genre must never select a finished pattern.
 * There is no `ambientPattern` / `technoPattern` / `jazzPattern` here and there
 * must never be one. Every output is a continuous weight that the rhythm
 * generator blends, so any mix of the three sliders produces a coherent set of
 * rules rather than a crossfade between three canned grooves.
 *
 * The formulas are deliberately not a plain linear sum of the three values:
 * interaction terms (groove suppressing pulse, ambient stretching intervals)
 * are what make the middle of the space sound like its own thing instead of an
 * average.
 */

/** Sliders are independent, so they are normalised to sum to 1. */
export function normaliseGenre(input: GenreBlendInput): GenreBlend {
  const ambient = Math.max(0, input.ambient);
  const techno = Math.max(0, input.techno);
  const jazz = Math.max(0, input.jazz);
  const total = ambient + techno + jazz;

  // All-zero is a legitimate UI state and must not divide by zero or mute the app.
  if (total <= 0) {
    return { ambient: 1 / 3, techno: 1 / 3, jazz: 1 / 3 };
  }
  return { ambient: ambient / total, techno: techno / total, jazz: jazz / total };
}

export interface GenreMapperInputs {
  genre: GenreBlend;
  /** 0..1, from Music controls. Low groove loosens the grid. */
  grooveStrength: number;
  /** 0..1, from Music controls. */
  rhythmicComplexity: number;
}

export function mapGenreToFeatures(inputs: GenreMapperInputs): GenreFeatures {
  const { ambient: a, techno: t, jazz: j } = inputs.genre;
  const groove = clamp(inputs.grooveStrength, 0, 1);
  const complexity = clamp(inputs.rhythmicComplexity, 0, 1);

  // Techno asserts the pulse, jazz implies it, ambient dissolves it. Groove
  // scales the whole thing: low groove makes even a techno-heavy mix float.
  const pulseStrength = clamp((t * 0.95 + j * 0.3 - a * 0.5 + 0.15) * (0.35 + 0.65 * groove), 0, 1);

  // Regularity is what makes techno sound machine-like; jazz and ambient both
  // pull away from the grid, for different reasons.
  const rhythmicRegularity = clamp(
    (t * 0.9 + 0.2 - j * 0.45 - a * 0.3) * (0.4 + 0.6 * groove),
    0,
    1,
  );

  // Techno repeats and mutates in controlled steps; jazz rewrites more freely;
  // ambient changes rarely but arbitrarily when it does.
  const patternRepetition = clamp(t * 0.8 + 0.15 - j * 0.4 - a * 0.15, 0, 1);
  const patternMutation = clamp(0.12 + j * 0.5 + a * 0.28 + t * 0.22 + complexity * 0.2, 0, 1);

  const subdivisionBias = clamp(t * 0.5 + j * 0.7 + a * 0.1 + complexity * 0.25, 0, 1);
  const syncopation = clamp(j * 0.85 + a * 0.15 - t * 0.2 + complexity * 0.2, 0, 1);
  const laybackAmount = clamp(j * 0.8 + a * 0.15 - t * 0.35, 0, 1);
  const ghostNoteTendency = clamp(j * 0.8 + a * 0.1 + complexity * 0.15, 0, 1);

  // Polymeter is where "techno with low groove" becomes broken rather than
  // four-on-the-floor: the interaction term, not techno alone, drives it.
  const polymeterTendency = clamp(t * 0.55 * (1 - groove) + j * 0.35 + a * 0.2, 0, 1);

  const silenceTendency = clamp(a * 0.7 + j * 0.18 - t * 0.3, 0, 1);

  // Ambient thins events out, techno and jazz fill them in. This is a genre
  // bias on top of the user's Density, not a replacement for it.
  const eventDensityBias = clamp(0.5 + t * 0.35 + j * 0.3 - a * 0.55, 0.1, 1);

  // Auto tempo is derived from genre only — never from Energy or input level.
  const tempoBias = clamp(0.5 + t * 0.4 - a * 0.3 + j * 0.05, 0, 1);

  return {
    pulseStrength,
    rhythmicRegularity,
    patternRepetition,
    patternMutation,
    subdivisionBias,
    syncopation,
    laybackAmount,
    ghostNoteTendency,
    polymeterTendency,
    silenceTendency,
    eventDensityBias,
    tempoBias,
  };
}
